import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  ClassSelector,
  EcommerceTrackingService,
  FormService,
  ScrollService,
  TypedTemplateDirective,
  WindowRef,
} from '@boels/helpers';
import { AddressValidationResult, CheckoutInputLimitEnum } from '@boels/models';
import { DeliveryFormState, ShippingDetailsService } from '@boels/shared-state';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowRight,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { NgSelectModule } from '@ng-select/ng-select';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { AddressValidationComponent } from '../../address-validation/address-validation.component';
import { FormErrorsComponent } from '../../form/form-errors/form-errors.component';
import { TextInputComponent } from '../../form/input/text-input.component';
import { TextareaComponent } from '../../form/textarea/textarea.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, take, tap } from 'rxjs';
import { PhoneComponent } from '../../phone/phone.component';
import { ButtonComponent } from '../../button/button.component';

@Component({
  selector: 'boels-checkout-delivery',
  templateUrl: './checkout-delivery.component.html',
  styleUrls: ['./checkout-delivery.component.scss'],
  providers: [
    FormService,
    ScrollService,
    ShippingDetailsService,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['checkout', 'form', 'deliveryMode'],
    },
  ],
  imports: [
    TextInputComponent,
    NgSelectModule,
    FormErrorsComponent,
    TranslocoModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TextareaComponent,
    FontAwesomeModule,
    AddressValidationComponent,
    TypedTemplateDirective,
    PhoneComponent,
    ButtonComponent,
  ],
})
export class CheckoutDeliveryComponent implements OnInit {
  // Dependencies
  private router = inject(Router);
  private scrollService = inject(ScrollService);
  private elementRef = inject(ElementRef);
  private shippingDetailsService = inject(ShippingDetailsService);
  private ecommerceTrackingService = inject(EcommerceTrackingService);
  private destroyRef = inject(DestroyRef);
  private winRef = inject(WindowRef);

  // Variables
  readonly faChevronRightIcon = faChevronRight;
  readonly faChevronLeftIcon = faChevronLeft;
  readonly maxInputLengths = CheckoutInputLimitEnum;

  readonly deliveryFormState: Signal<DeliveryFormState> = signal(
    this.shippingDetailsService.defaultState,
  );

  ngOnInit(): void {
    this.shippingDetailsService
      .prefillDeliveryForm(this.deliveryFormState())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.deliveryFormState()
      .deliveryForm()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.deliveryFormState().validationResult.set(undefined);
        this.deliveryFormState().continueWithValidationIssue.set(false);
      });
  }

  getStateMetaData<K extends keyof DeliveryFormState>(key: K) {
    return this.deliveryFormState()[key];
  }

  getControl = (key: string, nestedKey?: string) =>
    this.shippingDetailsService.getFormItem<FormControl>(
      this.deliveryFormState().deliveryForm(),
      key,
      nestedKey,
    );

  replaceWithSuggestion(form: FormGroup, data: AddressValidationResult): void {
    form.patchValue({
      ...data.correctedAddressList,
    });
  }

  onSubmit(deliveryFormState: DeliveryFormState) {
    if (!deliveryFormState.deliveryForm().valid) {
      deliveryFormState.deliveryForm().markAllAsTouched();
      this.scrollService.scrollToElement(
        ClassSelector.INVALID_FORM_INPUT,
        this.elementRef,
      );
      return;
    }

    this.shippingDetailsService
      .submitDeliveryForm(deliveryFormState)
      .pipe(
        tap((result) => (result ? this.goNext() : undefined)),
        catchError((error) => {
          this.winRef.nativeWindow?.scrollTo({ top: 0, behavior: 'smooth' });
          this.shippingDetailsService.displayError(error);
          return EMPTY;
        }),
        take(1),
      )
      .subscribe();
  }

  goBack() {
    this.router.navigate(['checkout/delivery-mode']);
  }

  goNext() {
    this.winRef.nativeWindow?.scrollTo({ top: 0, behavior: 'smooth' });
    this.ecommerceTrackingService.setCheckoutOptionEvent('delivery');
    this.router.navigate(['checkout/review-order']);
  }

  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faArrowRight = faArrowRight;
}
